import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { ChevronDown, Loader2 } from 'lucide-react'

export interface InlinePillOption {
  value: string
  label: string
}

/** Text + background classes for a single option value, e.g. { text: 'text-red-600', bg: 'bg-red-100' } */
export interface InlinePillColor {
  text: string

}

interface InlinePillSelectProps {
  value: string
  options: readonly InlinePillOption[]
  onChange: (value: string) => void | Promise<void>
  colorClassName?: string
  /** Optional per-value colors, e.g. { HIGH: { text: 'text-red-600', bg: 'bg-red-100' } }.
   *  Falls back to colorClassName + transparent bg for any value not present. */
  colorMap?: Record<string, InlinePillColor>
  disabled?: boolean
}

/** Small inline "pill" dropdown used for quick-editing a single field (e.g. task type, priority). */
const InlinePillSelect = ({
  value,
  options,
  onChange,
  colorClassName = 'text-neutral-faded',
  colorMap,
  disabled,
}: InlinePillSelectProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [menuPos, setMenuPos] = useState<{ top: number; left: number; width: number } | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const buttonRef = useRef<HTMLButtonElement | null>(null)
  const menuRef = useRef<HTMLDivElement | null>(null)

  // Close on outside click — check both the trigger and the portaled menu.
  useEffect(() => {
    if (!isOpen) return
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node
      if (
        containerRef.current &&
        !containerRef.current.contains(target) &&
        menuRef.current &&
        !menuRef.current.contains(target)
      ) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  // Compute menu position relative to the viewport whenever it opens,
  // and keep it in sync on scroll/resize while open.
  useLayoutEffect(() => {
    if (!isOpen) return

    const updatePosition = () => {
      const rect = buttonRef.current?.getBoundingClientRect()
      if (!rect) return
      setMenuPos({
        top: rect.bottom + 4, // mt-1 equivalent
        left: rect.left,
        width: Math.max(rect.width, 120),
      })
    }

    updatePosition()
    window.addEventListener('scroll', updatePosition, true)
    window.addEventListener('resize', updatePosition)
    return () => {
      window.removeEventListener('scroll', updatePosition, true)
      window.removeEventListener('resize', updatePosition)
    }
  }, [isOpen])

  // Close on escape for good measure.
  useEffect(() => {
    if (!isOpen) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false)
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen])

  const selectedLabel = options.find((o) => o.value === value)?.label ?? value
  const selectedColor = colorMap?.[value]

  const handleSelect = async (nextValue: string) => {
    setIsOpen(false)
    if (nextValue === value) return
    try {
      setIsSaving(true)
      await onChange(nextValue)
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div ref={containerRef} className="relative inline-block">
      <button
        ref={buttonRef}
        type="button"
        disabled={disabled || isSaving}
        onClick={(e) => {
          e.stopPropagation()
          setIsOpen((prev) => !prev)
        }}
        className={`flex items-center gap-1 text-xs bg-primary-subtle font-medium px-1.5 py-0.5 rounded-md transition-colors disabled:opacity-50 ${
          selectedColor
            ? `${selectedColor.text}  hover:opacity-80`
            : `${colorClassName} hover:bg-primary-variant`
        }`}
      >
        {isSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : selectedLabel}
        <ChevronDown className="w-3 h-3" />
      </button>

      {isOpen &&
        menuPos &&
        createPortal(
          <div
            ref={menuRef}
            style={{
              position: 'fixed',
              top: menuPos.top,
              left: menuPos.left,
              width: menuPos.width,
              zIndex: 9999,
            }}
            className="rounded-lg bg-primary-light border border-primary-subtle shadow-lg py-1"
          >
            {options.map((option) => {
              const optionColor = colorMap?.[option.value]
              const isSelected = option.value === value
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleSelect(option.value)
                  }}
                  className={`w-full text-left px-3 py-1.5 text-xs transition-colors flex items-center gap-2 ${
                    optionColor
                      ? `${optionColor.text} hover:opacity-80`
                      : `text-neutral hover:bg-primary-variant ${isSelected ? 'text-color-accent font-medium' : ''}`
                  }`}
                >
                  <span className={isSelected ? 'font-medium' : ''}>{option.label}</span>
                </button>
              )
            })}
          </div>,
          document.body,
        )}
    </div>
  )
}

export default InlinePillSelect